import type { Tone } from "../../ds";
import type { Device } from "../../types";

/** Shared plain-English labels for the admin pages. */

export const ROLE_INFO: Record<string, { label: string; description: string; tone: Tone }> = {
  platform_admin: { label: "Platform admin", description: "Full control of the platform", tone: "accent" },
  report_admin: { label: "Report admin", description: "Manage all reports, data and screens", tone: "info" },
  report_owner: { label: "Report owner", description: "Build and publish reports they own", tone: "success" },
  report_viewer: { label: "Viewer", description: "View published reports", tone: "neutral" },
};

/** Roles a person can hold, most powerful first ("device" is for TVs only). */
export const PERSON_ROLES = ["platform_admin", "report_admin", "report_owner", "report_viewer"];

export function roleLabel(role: string): string {
  return ROLE_INFO[role]?.label ?? role.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

/** Short, readable id for a TV: the LAST 6 characters of its device id --
 * the same characters the TV prints under its activation code ("Screen ID"). */
export function deviceShortId(d: Pick<Device, "device_uuid">): string {
  return d.device_uuid.replace(/-/g, "").slice(-6).toUpperCase();
}

export function deviceLabel(d: Pick<Device, "name" | "device_uuid">): string {
  return d.name?.trim() || `New TV ${deviceShortId(d)}`;
}

export const DEVICE_STATUS: Record<Device["status"], { label: string; tone: Tone; hint: string }> = {
  active: { label: "Active", tone: "success", hint: "Approved and allowed to show reports." },
  pending: { label: "Waiting for approval", tone: "warning", hint: "This TV asked to join but hasn't been approved yet." },
  disabled: { label: "Disabled", tone: "neutral", hint: "Switched off by an admin. It can't show reports." },
  revoked: { label: "Revoked", tone: "danger", hint: "Access removed for good." },
};

export function errorText(e: unknown, fallback = "Please try again in a moment."): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

/** A TV counts as online when it checked in within this window (the TV app
 * sends a heartbeat every 5 minutes; token refreshes also count). */
export const ONLINE_WINDOW_MS = 15 * 60 * 1000;

export type ScreenState = "online" | "offline" | "never" | "signed-out" | "reconnect" | "pending" | "disabled" | "revoked";

export function isOnline(d: Pick<Device, "status" | "last_seen_at">): boolean {
  return d.status === "active" && !!d.last_seen_at && Date.now() - new Date(d.last_seen_at).getTime() < ONLINE_WINDOW_MS;
}

/** True while the 6-character code the TV shows can still be typed in. */
export function codeIsLive(d: Pick<Device, "activation_status" | "activation_expires_at" | "updated_at">): boolean {
  if (d.activation_status !== "requested") return false;
  const expires = d.activation_expires_at ? new Date(d.activation_expires_at).getTime() : new Date(d.updated_at).getTime() + 10 * 60000;
  return expires > Date.now();
}

/** An approved screen whose TV is showing a code to sign in again. */
export function wantsReconnect(d: Device): boolean {
  return d.status === "active" && d.activation_status === "requested";
}

export function screenState(d: Device): ScreenState {
  if (d.status === "pending") return "pending";
  if (d.status === "disabled") return "disabled";
  if (d.status === "revoked") return "revoked";
  if (wantsReconnect(d) && codeIsLive(d)) return "reconnect";
  if (isOnline(d)) return "online";
  if (d.signed_in === false) return "signed-out";
  return d.last_seen_at ? "offline" : "never";
}

export const SCREEN_STATE: Record<ScreenState, { label: string; tone: Tone; hint: string }> = {
  online: { label: "Online", tone: "success", hint: "Checked in within the last 15 minutes." },
  offline: { label: "Offline", tone: "neutral", hint: "Approved, but hasn't checked in for more than 15 minutes — switched off, asleep or not on the Reports screen." },
  never: { label: "Not seen yet", tone: "neutral", hint: "Approved, but hasn't checked in since online status was added. Older TV app versions report in only every few hours." },
  "signed-out": { label: "Signed out", tone: "warning", hint: "Its sign-in was reset. The TV shows a new code — approve it to sign it back in." },
  reconnect: { label: "Asking to sign in again", tone: "warning", hint: "The TV is showing a code right now. Approve it to sign it back in — it keeps its name, group and reports." },
  pending: { label: "Waiting for approval", tone: "warning", hint: "This TV asked to join but hasn't been approved yet." },
  disabled: { label: "Disabled", tone: "neutral", hint: "Switched off by an admin. It can't show reports until it's enabled again." },
  revoked: { label: "Revoked", tone: "danger", hint: "Access removed for good. Delete it to let the TV join again as a new screen." },
};
